import React from 'react';
import { MenuItem } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { Star } from 'lucide-react';

interface MenuItemCardProps {
  item: MenuItem;
  storeCurrency: string;
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({ item, storeCurrency }) => {
  const { t } = useLanguage();
  
  const formatPrice = (price: number) => {
    return price.toLocaleString('tr-TR', {
      minimumFractionDigits: 2
    });
  };
  
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden flex menu-item transition-all duration-300 hover:shadow-md">
      {/* Item Image */}
      {item.imageUrl ? (
        <div className="w-24 h-24 flex-shrink-0 bg-gray-200 rounded-md overflow-hidden mr-4">
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = '/api/placeholder/96/96';
            }}
          />
        </div>
      ) : (
        <div className="w-24 h-24 flex-shrink-0 bg-gray-200 rounded-md mr-4 flex items-center justify-center text-gray-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      
      {/* Item Content */}
      <div className="flex-1 p-3">
        <div className="flex justify-between items-start mb-1">
          <div className="flex items-center">
            <h3 className="font-medium text-gray-900">{item.name}</h3>
            {item.isMostPreferred && (
              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full flex items-center">
                <Star className="h-3 w-3 mr-1 fill-current" />
                {t('menuItem.popular')}
              </span>
            )}
          </div>
          <div className="px-2 py-1 bg-blue-600 text-white rounded-md text-sm font-medium">
            {formatPrice(item.price)} {storeCurrency}
          </div>
        </div>
        
        {item.description && (
          <p className="text-sm text-gray-500 line-clamp-2 mb-2">{item.description}</p>
        )}
        
        {/* Status and Attributes */}
        <div className="flex flex-wrap gap-1 mt-auto">
          {item.soldOut && (
            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full flex items-center">
              {t('menuItem.soldOut')}
            </span>
          )}
          
          {item.outOfOrder && (
            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              {t('menuItem.outOfOrder')}
            </span>
          )}
          
          {item.attributes && item.attributes.map(attr => (
            <span 
              key={attr} 
              className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full"
            >
              {attr}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MenuItemCard;